import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tasksApi, Task, TaskFilters, UpdateTaskRequest } from '@/api'

/**
 * Query keys for task-related queries
 */
export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (filters?: TaskFilters) => [...taskKeys.lists(), filters] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  my: () => [...taskKeys.all, 'my'] as const,
  upcoming: (days: number) => [...taskKeys.all, 'upcoming', days] as const,
}

/**
 * Hook to fetch paginated list of tasks with filters
 */
export function useTasks(filters?: TaskFilters) {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: () => tasksApi.list(filters),
  })
}

/**
 * Hook to fetch a single task by ID
 */
export function useTask(id: string) {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => tasksApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to fetch tasks assigned to the current user
 */
export function useMyTasks() {
  return useQuery({
    queryKey: taskKeys.my(),
    queryFn: () => tasksApi.myTasks(),
  })
}

/**
 * Hook to fetch upcoming tasks (due within specified days)
 */
export function useUpcomingTasks(days: number = 7) {
  return useQuery({
    queryKey: taskKeys.upcoming(days),
    queryFn: () => {
      const today = new Date()
      const futureDate = new Date(today)
      futureDate.setDate(today.getDate() + days)

      return tasksApi.list({
        due_after: today.toISOString().split('T')[0],
        due_before: futureDate.toISOString().split('T')[0],
      })
    },
  })
}

/**
 * Hook to update a task
 */
export function useUpdateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskRequest }) =>
      tasksApi.update(id, data),
    onSuccess: (updatedTask) => {
      // Update the task in the cache
      queryClient.setQueryData(taskKeys.detail(updatedTask.id), updatedTask)
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() })
      queryClient.invalidateQueries({ queryKey: taskKeys.my() })
    },
  })
}

/**
 * Hook to complete a task (convenience wrapper)
 */
export function useCompleteTask() {
  const updateTask = useUpdateTask()

  return useMutation({
    mutationFn: (id: string) =>
      updateTask.mutateAsync({ id, data: { status: 'completed' } }),
  })
}

/**
 * Hook to change task status
 */
export function useChangeTaskStatus() {
  const updateTask = useUpdateTask()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: Task['status'] }) =>
      updateTask.mutateAsync({ id, data: { status } }),
  })
}

/**
 * Hook to delete a task
 */
export function useDeleteTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => tasksApi.delete(id),
    onSuccess: (_data, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: taskKeys.detail(id) })
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() })
      queryClient.invalidateQueries({ queryKey: taskKeys.my() })
    },
  })
}
